﻿import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import { fetchRequest } from '@tk/utilities/tk.fetch';

type AsyncResponse = {
    dataAsHtml: string;
};

export default class KDMZVisibilityController extends TKCustomElementFactory {
    private controls: HTMLInputElement[] = [];

    /**
     * Bereits im HTML vorhandenes Hidden-Feld für die Submit-Aktion.
     */
    private submitActionField?: HTMLInputElement;

    /**
     * Submit-Buttons, die die Submit-Aktion setzen.
     */
    private submitButtons: HTMLButtonElement[] = [];

    /**
     * Kontofelder, deren zugehörige Kostenstellenfelder über
     * data-accounting-visibility definiert werden.
     */
    private accountingFields: HTMLInputElement[] = [];

    /**
     * Aktive Async-Requests pro Visibility-Target.
     *
     * Wird verwendet, um bei einer erneuten Änderung eines Controls
     * den vorherigen Request als veraltet zu markieren.
     */
    private asyncRequests = new Map<HTMLElement, symbol>();

    constructor() {
        super();
    }

    connectedCallback(): void {
        this.controls = Array.from(
            this.querySelectorAll<HTMLInputElement>(
                'input[type="checkbox"][data-visibility-target], ' +
                    'input[type="radio"][data-visibility-target], ' +
                    'input[type="number"][data-visibility-target]',
            ),
        );

        this.controls.forEach((control) => {
            control.addEventListener('change', this.handleChange);
            control.addEventListener('input', this.handleChange);
        });

        // Kontofelder anhand des Data-Attributs ermitteln.
        this.accountingFields = Array.from(
            document.querySelectorAll<HTMLInputElement>('input[data-accounting-visibility]'),
        );

        // Änderungen der Kontonummern ebenfalls berücksichtigen.
        this.accountingFields.forEach((field) => {
            field.addEventListener('change', this.handleChange);
            field.addEventListener('input', this.handleChange);
        });

        // Ursprünglichen Required-Zustand speichern.
        this.rememberRequiredFields();

        // Initialen Zustand setzen.
        this.update();
    }

    disconnectedCallback(): void {
        this.controls.forEach((control) => {
            control.removeEventListener('change', this.handleChange);
            control.removeEventListener('input', this.handleChange);
        });

        this.accountingFields.forEach((field) => {
            field.removeEventListener('change', this.handleChange);
            field.removeEventListener('input', this.handleChange);
        });

        this.submitButtons.forEach((button) => {
            button.removeEventListener('click', this.handleSubmitButtonClick);
        });

        this.asyncRequests.clear();

        this.controls = [];
        this.accountingFields = [];
        this.submitButtons = [];
        this.submitActionField = undefined;
    }

    private handleChange = (event: Event): void => {
        this.update();

        if (event.type !== 'change' || !(event.currentTarget instanceof HTMLInputElement)) {
            return;
        }

        const control = event.currentTarget;

        if (control.type !== 'checkbox' || !control.checked) {
            return;
        }

        this.autofillFields(control);
    };

    private autofillFields(control: HTMLInputElement): void {
        const targetName = control.dataset.visibilityTarget;
        const form = this.closest('form');

        if (!targetName || !form) {
            return;
        }

        const target = form.querySelector<HTMLElement>(`[data-visibility-id="${CSS.escape(targetName)}"]`);

        if (!target) {
            return;
        }

        if (target.hasAttribute('data-visibility-restore-defaults')) {
            target.querySelectorAll<HTMLInputElement>('input[type="text"]').forEach((field) => {
                if (field.value.trim() !== '' || field.defaultValue === '') {
                    return;
                }

                field.value = field.defaultValue;
                field.dispatchEvent(new Event('input', { bubbles: true }));
            });
        }

        target.querySelectorAll<HTMLInputElement>('[data-autofill-from]').forEach((targetField) => {
            if (targetField.value.trim() !== '') {
                return;
            }

            const sourceIds = targetField.dataset.autofillFrom?.split(/\s+/).filter(Boolean) ?? [];
            const sourceValues = sourceIds
                .map((sourceId) => form.querySelector<HTMLInputElement>(`#${CSS.escape(sourceId)}`)?.value.trim() ?? '')
                .filter(Boolean);

            if (sourceValues.length === 0) {
                return;
            }

            targetField.value = sourceValues.join(' ');
            targetField.dispatchEvent(new Event('input', { bubbles: true }));
        });
    }

    /**
     * Merkt sich für jedes Formularfeld innerhalb eines
     * Visibility-Targets, ob es ursprünglich required war.
     *
     * Der Zustand wird direkt am Feld gespeichert.
     * Dadurch kann required später zuverlässig wiederhergestellt werden,
     * auch nachdem das Attribut entfernt wurde.
     */
    private rememberRequiredFields(): void {
        const targets = document.querySelectorAll<HTMLElement>('[data-visibility-id]');

        targets.forEach((target) => {
            const fields = target.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
                'input, select, textarea',
            );

            fields.forEach((field) => {
                if (field.hasAttribute('required')) {
                    field.dataset.originalRequired = 'true';
                }
            });
        });
    }

    private update(): void {
        const targetNames = new Set<string>();

        // Alle verwendeten Targets ermitteln.
        this.controls.forEach((control) => {
            const targetName = control.dataset.visibilityTarget;

            if (targetName) {
                targetNames.add(targetName);
            }
        });

        // Targets ein- oder ausblenden.
        targetNames.forEach((targetName) => {
            const targets = document.querySelectorAll<HTMLElement>(`[data-visibility-id="${targetName}"]`);

            const controlsForTarget = this.controls.filter(
                (control) => control.dataset.visibilityTarget === targetName,
            );

            const shouldShow = controlsForTarget.some((control) => {
                // Bestehende Logik für Checkboxen und Radio-Buttons.
                if (control.type === 'checkbox' || control.type === 'radio') {
                    return control.checked;
                }

                // Logik für numerische Inputfelder:
                // Nur Werte > 0 aktivieren das Target.
                if (control.type === 'number') {
                    const value = control.value.trim();

                    if (value === '') {
                        return false;
                    }

                    const numericValue = Number(value);

                    return Number.isFinite(numericValue) && numericValue > 0;
                }

                return false;
            });

            targets.forEach((target) => {
                const wasHidden = target.hasAttribute('hidden');

                if (shouldShow) {
                    target.removeAttribute('hidden');

                    // Async-Inhalt laden bzw. aktualisieren.
                    this.updateAsyncTarget(target);
                } else {
                    // Nur beim Übergang von sichtbar zu versteckt leeren.
                    if (!wasHidden) {
                        this.resetFields(target);
                    }

                    target.setAttribute('hidden', '');

                    // Eventuell noch laufenden Async-Request verwerfen.
                    this.invalidateAsyncRequest(target);
                }
            });
        });

        // Required-Zustände aktualisieren.
        this.updateRequiredFields();

        // Spezielle Visibility-Regeln aktualisieren.
        this.updateSpecialVisibility();

        // CSS-Klassen für den Body aktualisieren.
        this.updateBodyClasses();
    }

    /**
     * Aktualisiert CSS-Klassen auf dem <body>-Element anhand
     * der überwachten Formularfelder.
     *
     * Ein Formularfeld kann über data-visibility-body-class definieren,
     * welcher Klassenname verwendet werden soll.
     *
     * Bei Checkboxen und Radio-Buttons wird die Klasse anhand des Wertes
     * erzeugt. Bei Number-Inputs wird der aktuelle Zahlenwert verwendet.
     */
    private updateBodyClasses(): void {
        const body = document.body;

        if (!body) {
            return;
        }

        this.controls.forEach((control) => {
            const className = control.dataset.visibilityBodyClass;

            if (!className) {
                return;
            }

            // Checkboxen und Radio-Buttons.
            if (control.type === 'checkbox' || control.type === 'radio') {
                const valueClass = control.value ? `${className}-${control.value}` : className;

                if (control.checked) {
                    body.classList.add(valueClass);
                } else {
                    body.classList.remove(valueClass);
                }

                return;
            }

            // Number-Inputs.
            if (control.type === 'number') {
                const value = control.value.trim();

                // Alle vorhandenen Value-Klassen dieses Controls entfernen.
                const existingClasses = Array.from(body.classList).filter((bodyClass) =>
                    bodyClass.startsWith(`${className}-`),
                );

                existingClasses.forEach((bodyClass) => {
                    body.classList.remove(bodyClass);
                });

                // Bei leerem Wert keine Klasse setzen.
                if (value === '') {
                    return;
                }

                const numericValue = Number(value);

                // Nur gültige Werte als CSS-Klasse verwenden.
                if (!Number.isFinite(numericValue)) {
                    return;
                }

                body.classList.add(`${className}-${value}`);
            }
        });
    }

    /**
     * Enthält spezielle Visibility-Regeln, die nicht über
     * data-visibility-target gesteuert werden.
     *
     * Weitere Sonderfälle können hier zentral ergänzt werden.
     */
    private updateSpecialVisibility(): void {
        this.updateFilialanschlussVisibility();
        this.updateAccountingVisibility();
        this.updateDisabledTargets();
        this.updateEnabledTargets();

        // Weitere Sonderfälle hier ergänzen:
        // this.updateAnotherSpecialVisibility();
    }

    /**
     * Steuert die spezielle Progress-Ansicht für den Filialanschluss.
     *
     * Wenn "filialanschluss-yes" ausgewählt ist:
     * - opc-progress-view-wrapper--short wird versteckt.
     * - opc-progress-view-wrapper--long wird angezeigt.
     *
     * Bei Abwahl wird der Zustand umgekehrt.
     */
    private updateFilialanschlussVisibility(): void {
        const filialanschlussYes = document.querySelector<HTMLInputElement>(
            'input[type="radio"][name="filialanschluss"][value="yes"], ' +
                'input[type="radio"][id="filialanschluss-yes"]',
        );

        const shortWrapper = document.querySelector<HTMLElement>('.opc-progress-view-wrapper--short');
        const longWrapper = document.querySelector<HTMLElement>('.opc-progress-view-wrapper--long');

        if (!filialanschlussYes || !shortWrapper || !longWrapper) {
            return;
        }

        const isSelected = filialanschlussYes.checked;

        shortWrapper.toggleAttribute('hidden', isSelected);
        longWrapper.toggleAttribute('hidden', !isSelected);
    }

    /**
     * Steuert die Sichtbarkeit der Kostenstellenfelder anhand
     * der ersten Ziffer des zugehörigen Kontos.
     *
     * Die Zuordnung erfolgt über data-accounting-visibility am Kontofeld.
     * Der Attributwert entspricht der ID des Kostenstellenfeldes.
     *
     * Die Kostenstelle wird nur angezeigt, wenn die Kontonummer
     * mit 3 oder 4 beginnt.
     */
    private updateAccountingVisibility(): void {
        this.accountingFields.forEach((accountField) => {
            const costCenterFieldId = accountField.dataset.accountingVisibility;

            if (!costCenterFieldId) {
                return;
            }

            const costCenterField = document.getElementById(costCenterFieldId);
            const costCenterColumn = costCenterField?.closest<HTMLElement>('.opc-col');

            if (!costCenterColumn) {
                return;
            }

            const firstCharacter = accountField.value.charAt(0);

            costCenterColumn.toggleAttribute('hidden', firstCharacter !== '3' && firstCharacter !== '4');
        });
    }

    /**
     * Aktualisiert den asynchronen Inhalt eines Visibility-Targets.
     *
     * Ein Target kann beispielsweise so definiert werden:
     *
     * <div
     *     data-visibility-id="attachments-Terminal1"
     *     data-visibility-async-url="/attachments"
     *     hidden
     * >
     * </div>
     *
     * Die zugehörigen Controls liefern die Payload-Parameter:
     *
     * data-visibility-target-async-url-param="article"
     * data-visibility-target-async-url-param-value="1234"
     *
     * Mehrere gleiche Parameter werden zu einem String zusammengefasst.
     */
    private updateAsyncTarget(target: HTMLElement): void {
        const url = target.dataset.visibilityAsyncUrl;

        if (!url) {
            return;
        }

        const targetName = target.dataset.visibilityId;

        if (!targetName) {
            return;
        }

        const controlsForTarget = this.controls.filter((control) => control.dataset.visibilityTarget === targetName);

        const delimiter = target.dataset.visibilityAsyncParamDelimiter ?? ',';
        const payload = this.collectAsyncPayload(controlsForTarget, delimiter);

        this.loadAsyncTarget(target, url, payload);
    }

    /**
     * Sammelt die Parameter für einen Async-Request.
     *
     * Gleiche Parameternamen werden zusammengefasst.
     *
     * Beispiel:
     * article=1234
     * article=3456
     * kategorie=terminal
     *
     * ergibt:
     * {
     *     article: '1234,3456',
     *     kategorie: 'terminal'
     * }
     */
    private collectAsyncPayload(controls: HTMLInputElement[], delimiter: string): Record<string, string> {
        const valuesByParameter = new Map<string, string[]>();

        controls.forEach((control) => {
            const paramName = control.dataset.visibilityTargetAsyncUrlParam;
            const paramValue = control.dataset.visibilityTargetAsyncUrlParamValue;

            if (!paramName || !paramValue) {
                return;
            }

            // Bei Number-Inputs nur Werte > 0 berücksichtigen.
            if (control.type === 'number') {
                const quantity = Number(control.value);

                if (!Number.isFinite(quantity) || quantity <= 0) {
                    return;
                }
            }

            const values = valuesByParameter.get(paramName) ?? [];

            values.push(paramValue);
            valuesByParameter.set(paramName, values);
        });

        return Object.fromEntries(
            Array.from(valuesByParameter.entries()).map(([name, values]) => [name, values.join(delimiter)]),
        );
    }

    /**
     * Führt den Async-Request für ein Target aus.
     *
     * Ein neuer Request macht den vorherigen Request für dasselbe
     * Target ungültig. Dadurch kann ein verspätetes Ergebnis eines
     * älteren Requests nicht mehr den aktuellen Inhalt überschreiben.
     */
    private loadAsyncTarget(target: HTMLElement, url: string, payload: Record<string, string>): void {
        const requestId = Symbol();

        this.asyncRequests.set(target, requestId);

        fetchRequest({
            requestURL: url,
            payload,
            resolveHandler: (result: AsyncResponse) => {
                if (this.asyncRequests.get(target) !== requestId) {
                    return;
                }

                this.asyncRequests.delete(target);

                target.innerHTML = result.dataAsHtml;

                this.rememberRequiredFields();
                this.updateRequiredFields();
            },
        });
    }

    /**
     * Macht einen eventuell laufenden Request für ein Target ungültig.
     *
     * Da fetchRequest hier keinen AbortController entgegennimmt,
     * wird der Request nicht technisch abgebrochen.
     * Sein Ergebnis wird aber ignoriert, wenn inzwischen ein neuer
     * Request gestartet wurde oder das Target ausgeblendet wurde.
     */
    private invalidateAsyncRequest(target: HTMLElement): void {
        this.asyncRequests.delete(target);
    }

    /**
     * Leert alle Formularfelder innerhalb eines ausgeblendeten Bereichs.
     *
     * Hidden-Felder werden grundsätzlich nicht verändert.
     */
    private resetFields(target: HTMLElement): void {
        const fields = target.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
            'input, select, textarea',
        );

        fields.forEach((field) => {
            if (field instanceof HTMLInputElement) {
                // Hidden-Felder niemals zurücksetzen.
                if (field.type === 'hidden') {
                    return;
                }

                if (field.type === 'checkbox' || field.type === 'radio') {
                    field.checked = false;
                } else {
                    field.value = '';
                }
            } else if (field instanceof HTMLSelectElement) {
                field.selectedIndex = -1;

                if (field.closest('.tk-form-select--filled')) {
                    field.closest('.tk-form-select--filled')?.classList.remove('tk-form-select--filled');
                }
            } else if (field instanceof HTMLTextAreaElement) {
                field.value = '';
            }
        });
    }

    /**
     * Entfernt required aus versteckten Bereichen
     * und stellt es bei sichtbaren Bereichen wieder her.
     *
     * Es werden ausschließlich Felder berücksichtigt,
     * die beim Initialisieren ursprünglich required waren.
     */
    private updateRequiredFields(): void {
        const fields = document.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
            '[data-visibility-id] input, ' + '[data-visibility-id] select, ' + '[data-visibility-id] textarea',
        );

        fields.forEach((field) => {
            // Ursprünglich nicht required: nichts verändern.
            if (field.dataset.originalRequired !== 'true') {
                return;
            }

            const hiddenParent = field.closest('[hidden]');

            if (hiddenParent) {
                field.removeAttribute('required');
            } else {
                field.setAttribute('required', '');
            }
        });
    }

    /**
     * Aktiviert oder deaktiviert Elemente abhängig vom Zustand
     * eines Radio-Buttons oder einer Checkbox.
     *
     * Das auslösende Feld definiert über data-disabled-target,
     * welche Elemente gesteuert werden sollen.
     *
     * Die Ziele werden über data-disabled-id identifiziert.
     *
     * Der Zustand wird bereits bei der Initialisierung berücksichtigt.
     */
    private updateDisabledTargets(): void {
        const controls = this.querySelectorAll<HTMLInputElement>(
            'input[type="checkbox"][data-disabled-target], ' + 'input[type="radio"][data-disabled-target]',
        );

        controls.forEach((control) => {
            const targetName = control.dataset.disabledTarget;

            if (!targetName) {
                return;
            }

            const targets = document.querySelectorAll<HTMLElement>(`[data-disabled-id="${targetName}"]`);

            targets.forEach((target) => {
                if (
                    target instanceof HTMLButtonElement ||
                    target instanceof HTMLInputElement ||
                    target instanceof HTMLSelectElement ||
                    target instanceof HTMLTextAreaElement
                ) {
                    target.disabled = control.checked;
                }
            });
        });
    }

    /**
     * Aktiviert Elemente abhängig vom Zustand eines Radio-Buttons
     * oder einer Checkbox.
     *
     * Das auslösende Feld definiert über data-enabled-target,
     * welche Elemente gesteuert werden sollen.
     *
     * Die Ziele werden über data-enabled-id identifiziert.
     *
     * Ist das auslösende Feld aktiviert, werden die Ziele aktiviert.
     * Bei Deaktivierung des auslösenden Feldes werden die Ziele deaktiviert.
     *
     * Der Zustand wird bereits bei der Initialisierung berücksichtigt.
     */
    private updateEnabledTargets(): void {
        const controls = this.querySelectorAll<HTMLInputElement>(
            'input[type="checkbox"][data-enabled-target], ' + 'input[type="radio"][data-enabled-target]',
        );

        controls.forEach((control) => {
            const targetName = control.dataset.enabledTarget;

            if (!targetName) {
                return;
            }

            const targets = document.querySelectorAll<HTMLElement>(`[data-enabled-id="${targetName}"]`);

            targets.forEach((target) => {
                if (
                    target instanceof HTMLButtonElement ||
                    target instanceof HTMLInputElement ||
                    target instanceof HTMLSelectElement ||
                    target instanceof HTMLTextAreaElement
                ) {
                    target.disabled = !control.checked;
                }
            });
        });
    }
}
